import React, { useEffect, useRef } from 'react';
import { ClearanceFeedbackMessage } from '../../clearance/clearance-feedback';

export interface PelecardResponse {
  StatusCode: string;
  transactionId: string;
  StatusDescription: string;
  ConfirmationKey: string;
  ApprovalNo: string;
  ParamX?: string;
}

interface PaymentIframeProps {
  src: string;
  successLink: string;
  failLink: string;
  onPaymentSuccess: (data: PelecardResponse) => void;
  onPaymentError: (data?: PelecardResponse) => void;
}

const PaymentIframe: React.FC<PaymentIframeProps> = ({
  src,
  successLink,
  failLink,
  onPaymentSuccess,
  onPaymentError,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Listen for postMessage from the iframe (clearance feedback page)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Validate the message structure
      if (event.data && event.data.type === 'CLEARANCE_FEEDBACK') {
        const message = event.data as ClearanceFeedbackMessage;
        console.log('Received postMessage from iframe:', message);

        const responseData: PelecardResponse = {
          transactionId: message.data?.transactionId || '',
          StatusCode:
            message.data?.statusCode || (message.isSuccess ? '000' : ''),
          StatusDescription: message.data?.statusDescription || '',
          ConfirmationKey: message.data?.confirmationKey || '',
          ApprovalNo: message.data?.approvalNo || '',
        };

        if (message.isSuccess) {
          onPaymentSuccess(responseData);
        } else {
          onPaymentError(responseData);
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [onPaymentSuccess, onPaymentError]);

  const handleLoad = (e: React.SyntheticEvent<HTMLIFrameElement>) => {
    const target = e.target as HTMLIFrameElement;
    try {
      // This will throw a SecurityError if the iframe is on a cross-origin domain
      // That's expected when the payment gateway page loads
      const currentHref = target?.contentWindow?.location?.href;

      if (currentHref?.includes(successLink)) {
        console.log(`Payment success URL detected: ${currentHref}`);
        const url = new URL(currentHref);
        const params = new URLSearchParams(url.search);

        const responseData: PelecardResponse = {
          transactionId: params.get('PelecardTransactionId') || '',
          StatusCode: params.get('PelecardStatusCode') || '',
          StatusDescription: params.get('StatusDescription') || '',
          ConfirmationKey: params.get('ConfirmationKey') || '',
          ApprovalNo: params.get('ApprovalNo') || '',
          ParamX: params.get('ParamX') || undefined,
        };

        if (responseData.StatusCode === '000') {
          //onPaymentSuccess(responseData);
        } else {
          //onPaymentError(responseData);
        }
      } else if (currentHref?.includes(failLink)) {
        console.log(`Payment fail URL detected: ${currentHref}`);
        const url = new URL(currentHref);
        const params = new URLSearchParams(url.search);

        const responseData: PelecardResponse = {
          transactionId: params.get('PelecardTransactionId') || '',
          StatusCode: params.get('PelecardStatusCode') || '',
          StatusDescription: params.get('StatusDescription') || '',
          ConfirmationKey: params.get('ConfirmationKey') || '',
          ApprovalNo: params.get('ApprovalNo') || '',
          ParamX: params.get('ParamX') || undefined,
        };

        //onPaymentError(responseData);
      }
    } catch (error) {
      // Cross-origin errors are expected when the iframe is on the payment gateway domain
      // This is normal during the payment process - don't treat as an error
      // Only log for debugging purposes
      if (error instanceof DOMException && error.name === 'SecurityError') {
        // Expected cross-origin error - iframe is still on payment gateway
        console.log(
          'Iframe on cross-origin domain (payment gateway) - waiting for redirect',
        );
      } else {
        // Unexpected error
        console.error('Unexpected error during iframe load:', error);
      }
    }
  };

  return (
    <iframe
      style={{ width: '100%', height: '100%' }}
      ref={iframeRef}
      src={src}
      title="Payment Gateway"
      sandbox="allow-forms allow-scripts allow-same-origin allow-top-navigation" // Optional but recommended
      onLoad={handleLoad}
      allow="payment"
    />
  );
};

export default PaymentIframe;
